import React, { useCallback, useEffect, useState } from 'react';
import { can, PERM } from '../utils';
import { Select } from '../hr/components/Controls';

export const money = v => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
export const pct = v => (v === null || v === undefined ? '—' : `${+(Number(v) * 100).toFixed(4)}%`);

// what the signed-in user may change (the Payroll permissions); view alone reads
export const usePerms = user => ({
  setup: can(user, PERM.PAYROLL_SETUP),
  people: can(user, PERM.PAYROLL_EMPLOYEES),
  inputs: can(user, PERM.PAYROLL_INPUTS),
  loans: can(user, PERM.PAYROLL_LOANS),
  runs: can(user, PERM.PAYROLL_RUNS),
  compensation: can(user, PERM.PAYROLL_COMPENSATION),
  payments: can(user, PERM.PAYROLL_PAYMENTS),
});

// load a screen's data once and again after a save
export function useLoad(fetcher) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => fetcher().then(d => { setData(d); setError(''); return d; }).catch(e => setError(e.message || 'Could not load')), [fetcher]);
  useEffect(() => { load(); }, [load]);
  return [data, setData, load, error];
}

export const Field = ({ label, help, children, full }) => (
  <label className={`field${full ? ' full' : ''}`}><span className="field-label">{label}</span>{children}{help && <span className="field-help">{help}</span>}</label>
);

export const Num = ({ value, onChange, disabled, step = 'any', ...rest }) => (
  <input type="number" step={step} value={value ?? ''} disabled={disabled} onChange={e => onChange(e.target.value === '' ? null : e.target.value)} {...rest} />
);

export const Sel = ({ value, onChange, options, blank, disabled }) => (
  <Select value={value ?? ''} disabled={disabled} onChange={e => onChange(e.target.value)}>
    {blank !== undefined && <option value="">{blank}</option>}
    {options.map(o => (typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
  </Select>
);

export const Tick = ({ v }) => (v ? <span style={{ color: 'var(--good)', fontWeight: 700 }}>✓</span> : <span className="muted">—</span>);

export const Panel = ({ title, note, actions, children }) => (
  <div className="panel" style={{ marginBottom: 16 }}>
    <div className="panel-title" style={{ justifyContent: 'space-between' }}><span>{title}</span><span style={{ display: 'flex', gap: 8 }}>{actions}</span></div>
    {note && <p className="muted small" style={{ margin: '-6px 0 12px' }}>{note}</p>}
    {children}
  </div>
);

const STATUS_TONE = { Draft: '#64748b', Submitted: '#2563eb', Returned: '#d97706', Approved: '#16a34a', Active: '#16a34a', Rejected: '#dc2626',
  Cancelled: '#64748b', Paused: '#d97706', Closed: '#334155' };
export const Status = ({ s }) => (
  <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 99, fontSize: 12, fontWeight: 700, color: '#fff', background: STATUS_TONE[s] || '#64748b' }}>{s}</span>
);

export const PeriodPick = ({ value, periods, onChange }) => (
  <Select title="Payroll month" value={value || ''} onChange={e => onChange(e.target.value)} style={{ minWidth: 120 }}>{(periods || []).map(p => <option key={p} value={p}>{p}</option>)}</Select>
);

const ACTION_LABEL = { submit: 'Submit for approval', approve: 'Approve', return: 'Return for correction', reject: 'Reject', cancel: 'Cancel request',
  reopen: 'Reopen', pause: 'Pause deductions', resume: 'Resume deductions', close: 'Close loan', delete: 'Delete' };
const NEEDS_NOTE = ['return', 'reject'];

// the approval route, its history and the actions open to the viewer (the engine decides who may approve)
export function Approval({ route, history, actions, onAction, skip = [] }) {
  const [note, setNote] = useState('');
  const shown = (actions || []).filter(a => ACTION_LABEL[a] && !skip.includes(a));
  return (
    <div>
      {!!route?.length && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '6px 0 10px' }}>
          {route.map(s => (
            <span key={s.step} title={s.comment || ''} style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, border: '1px solid var(--line)',
              background: s.state === 'approved' ? '#dcfce7' : s.state === 'current' ? '#dbeafe' : s.state === 'returned' || s.state === 'rejected' ? '#fee2e2' : '#fff' }}>
              <b>{s.step}</b> · {s.state}{s.by ? ` · ${s.by}` : ''}
            </span>))}
        </div>)}
      {!!shown.length && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }} className="no-print">
          {shown.some(a => NEEDS_NOTE.includes(a) || a === 'cancel') && <input placeholder="Comment (needed to return or reject)" value={note} onChange={e => setNote(e.target.value)} style={{ minWidth: 240 }} />}
          {shown.map(a => (
            <button key={a} className={`btn small ${a === 'approve' || a === 'submit' ? 'primary' : a === 'reject' || a === 'delete' ? 'danger ghost' : ''}`}
                    onClick={() => onAction(a, note).then(() => setNote(''))}>{ACTION_LABEL[a]}</button>))}
        </div>)}
      {!!history?.length && (
        <details style={{ marginTop: 10 }}><summary className="muted small" style={{ cursor: 'pointer' }}>Approval history ({history.length})</summary>
          <table style={{ marginTop: 6 }}><tbody>{history.map((h, i) => (
            <tr key={i}><td className="small" style={{ whiteSpace: 'nowrap' }}>{h.at}</td><td className="small">{h.by}</td><td className="small"><b>{h.action}</b></td><td className="small">{h.note}</td></tr>))}</tbody></table>
        </details>)}
    </div>
  );
}
